// =======================================================
// CyberneticTopography — flowing horizontal data-strata
// =======================================================
//
// Port of the TechWolf "Cybernetic Topography 360" TD shader: a stack
// of 20 horizontal contour-lines warped by sines + value noise, with
// bright "data sparks" surfing along each line, plus a per-frame
// scanline, vignette and seam-fade for ribbon screens.

import * as THREE from 'three';
import { Generator } from './Generator.js';

const VERT = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`;

const FRAG = /* glsl */ `
  varying vec2 vUv;
  uniform float u_time;
  uniform vec2  uResolution;
  uniform vec3  uBg;
  uniform vec3  uMint;
  uniform vec3  uSpark;
  uniform float uSpeed;
  uniform float uLineCount;     // number of horizontal strata
  uniform float uAspect;        // overrides screen aspect (0 = use resolution)
  uniform float uLineThickness; // smoothstep edge in normalized y
  uniform float uSparkBoost;
  uniform float uScanline;
  uniform float uVignette;
  uniform float uExposure;

  float noiseT(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    float a = fract(sin(dot(i,                vec2(127.1, 311.7))) * 43758.5453);
    float b = fract(sin(dot(i + vec2(1,0),    vec2(127.1, 311.7))) * 43758.5453);
    float c = fract(sin(dot(i + vec2(0,1),    vec2(127.1, 311.7))) * 43758.5453);
    float d = fract(sin(dot(i + vec2(1,1),    vec2(127.1, 311.7))) * 43758.5453);
    return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
  }

  void main() {
    vec2 originalUV = vUv;
    float aspect = uAspect > 0.0 ? uAspect : (uResolution.x / uResolution.y);
    vec2 uv = originalUV;
    uv.x *= aspect;

    float t = u_time * uSpeed;

    vec3 col = uBg;

    float lines   = 0.0;
    float flashes = 0.0;

    float N = uLineCount;
    float thickHi = uLineThickness;
    float thickLo = uLineThickness * 0.12;

    for (float i = 0.0; i < 64.0; i++) {
      if (i >= N) break;
      float yCenter = (i / max(1.0, N - 1.0)) * 0.9 + 0.05;

      float wave1 = sin(uv.x * 0.8 + t + i * 0.1) * 0.1;
      float wave2 = noiseT(vec2(uv.x * 1.5 - t * 0.5, i * 0.5)) * 0.15;
      float totalWave = wave1 + wave2;

      // Amplitude grows toward the top/bottom edges, like a depth field.
      float dist = abs(originalUV.y - yCenter - totalWave * (originalUV.y * 2.0));
      float l = smoothstep(thickHi, thickLo, dist);

      float intensity = noiseT(vec2(uv.x * 3.0 + t, i)) * 0.5 + 0.5;
      lines += l * intensity;

      float sparkPhase = fract(uv.x * 0.2 - t * (1.0 + i * 0.1));
      flashes += l * smoothstep(0.05, 0.0, sparkPhase) * intensity;
    }

    col += lines   * uMint  * 0.8;
    col += flashes * uSpark * uSparkBoost;

    // Scanline
    float scan = sin(originalUV.y * uResolution.y * 2.0) * uScanline;
    col -= vec3(scan);

    // Seam fade for ribbon-wrap displays.
    float seamFade = smoothstep(0.0, 0.01, originalUV.x)
                   * smoothstep(1.0, 0.99, originalUV.x);
    col *= seamFade;

    // Vertical vignette.
    float vignY = abs(originalUV.y - 0.5) * 2.0;
    col *= 1.0 - smoothstep(0.6, 1.2, vignY) * uVignette;

    col = pow(max(col, 0.0), vec3(1.1));
    col *= uExposure;
    col += col * 0.5;
    col = clamp(col, 0.0, 1.0);

    gl_FragColor = vec4(col, 1.0);
  }
`;

export class CyberneticTopography extends Generator {
  constructor() {
    super({
      name: 'Cybernetic Topography',
      category: 'intense',
      schema: {
        speed:        { default: 0.25, min: 0,    max: 2,    step: 0.01, label: 'Flow speed' },
        lineCount:    { default: 20,   min: 4,    max: 64,   step: 1,    label: 'Strata count' },
        thickness:    { default: 0.008,min: 0.002,max: 0.04, step: 0.001,label: 'Line thickness' },
        sparkBoost:   { default: 3.0,  min: 0,    max: 8,    step: 0.05, label: 'Spark boost' },
        scanline:     { default: 0.04, min: 0,    max: 0.2,  step: 0.005,label: 'Scanline strength' },
        vignette:     { default: 1.0,  min: 0,    max: 2,    step: 0.01, label: 'Vignette' },
        exposure:     { default: 1.8,  min: 0.5,  max: 4,    step: 0.05, label: 'Exposure' },
        aspectMode:   { default: 'auto', type: 'select', label: 'Aspect',
                        options: [['auto','Match canvas'], ['ribbon','Ribbon 18.5:1']] },
        bg:           { default: '#020308', type: 'color', label: 'Background' },
        mint:         { default: '#3dffd1', type: 'color', label: 'Line color' },
        spark:        { default: '#ccfff2', type: 'color', label: 'Spark color' },
      }
    });
  }

  onEnter(scene, engine) {
    const geo = new THREE.PlaneGeometry(2, 2);
    const mat = new THREE.ShaderMaterial({
      vertexShader: VERT, fragmentShader: FRAG,
      uniforms: {
        u_time:         { value: 0 },
        uResolution:    { value: new THREE.Vector2(engine.width, engine.height) },
        uBg:            { value: new THREE.Color(this.v('bg')) },
        uMint:          { value: new THREE.Color(this.v('mint')) },
        uSpark:         { value: new THREE.Color(this.v('spark')) },
        uSpeed:         { value: this.v('speed') },
        uLineCount:     { value: this.v('lineCount') },
        uAspect:        { value: this.v('aspectMode') === 'ribbon' ? (10900 / 590) : 0 },
        uLineThickness: { value: this.v('thickness') },
        uSparkBoost:    { value: this.v('sparkBoost') },
        uScanline:      { value: this.v('scanline') },
        uVignette:      { value: this.v('vignette') },
        uExposure:      { value: this.v('exposure') },
      },
      transparent: false,
      depthWrite: false,
      depthTest: false,
    });
    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.frustumCulled = false;
    scene.add(this.mesh);
    this.track(this.mesh);
  }

  onResize(w, h) {
    if (this.mesh) this.mesh.material.uniforms.uResolution.value.set(w, h);
  }

  onParamChange(key, val) {
    if (!this.mesh) return;
    const u = this.mesh.material.uniforms;
    if (key === 'speed')      u.uSpeed.value = val;
    if (key === 'lineCount')  u.uLineCount.value = val;
    if (key === 'thickness')  u.uLineThickness.value = val;
    if (key === 'sparkBoost') u.uSparkBoost.value = val;
    if (key === 'scanline')   u.uScanline.value = val;
    if (key === 'vignette')   u.uVignette.value = val;
    if (key === 'exposure')   u.uExposure.value = val;
    if (key === 'aspectMode') u.uAspect.value = val === 'ribbon' ? (10900 / 590) : 0;
    if (key === 'bg')    u.uBg.value.set(val);
    if (key === 'mint')  u.uMint.value.set(val);
    if (key === 'spark') u.uSpark.value.set(val);
  }

  update(dt, time, engine) {
    if (this.mesh) this.mesh.material.uniforms.u_time.value = time;
  }
}
